import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteCompetitionGuidance, getCompetitionGuidance, saveCompetitionGuidance } from '@/api/guidanceService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const errorMessage = (error: unknown) => {
  const res = (error as { response?: { data?: { message?: string } } })?.response;
  if (res?.data?.message) return res.data.message;
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

export const useCompetitionGuidance = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.pv2CompetitionGuidance });

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2CompetitionGuidance,
    queryFn: getCompetitionGuidance,
  });

  const saveMutation = useMutation({
    mutationFn: ({ formatId, payload }: { formatId: string; payload: { coachNote: string; nutrition: string } }) =>
      saveCompetitionGuidance(formatId, payload),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Mot du coach enregistré', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (formatId: string) => deleteCompetitionGuidance(formatId),
    onSuccess: () => {
      invalidate();
      toaster.create({ title: 'Mot du coach supprimé', type: 'success' });
    },
    onError: (error) => toaster.create({ title: 'Erreur', description: errorMessage(error), type: 'error' }),
  });

  return {
    items: data ?? [],
    isLoading,
    saveGuidance: saveMutation.mutateAsync,
    deleteGuidance: deleteMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
};
